import React, { useState, useEffect, useCallback } from 'react';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { kitApi } from '@/lib/api';

interface KitComboboxProps {
  selectedKits: string[];
  onKitsChange: (kits: string[]) => void;
  error?: string;
  className?: string;
}

export const KitCombobox: React.FC<KitComboboxProps> = ({
  selectedKits,
  onKitsChange,
  error,
  className
}) => {
  const [availableKits, setAvailableKits] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedKit, setSelectedKit] = useState('');

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((search: string) => {
      searchKits(search);
    }, 300),
    []
  );

  const searchKits = async (search: string = '') => {
    try {
      setLoading(true);
      console.log('KitCombobox: Searching kits with search term:', search);
      const kits = await kitApi.searchKits(search);
      console.log('KitCombobox: Kits response:', kits);

      // Handle different response formats
      let kitsArray = [];
      if (Array.isArray(kits)) {
        kitsArray = kits;
      } else if (kits && kits.data && Array.isArray(kits.data)) {
        kitsArray = kits.data;
      } else if (kits && typeof kits === 'object') {
        kitsArray = [kits];
      }

      setAvailableKits(kitsArray);
      console.log('KitCombobox: Set available kits:', kitsArray);
    } catch (error) {
      console.error('KitCombobox: Failed to search kits:', error);
      setAvailableKits([]);
    } finally {
      setLoading(false);
    }
  };

  // Load initial kits
  useEffect(() => {
    searchKits();
  }, []);

  // Handle search input change
  const handleSearch = (search: string) => {
    setSearchValue(search);
    debouncedSearch(search);
  };

  // Add selected kit
  const addKit = () => {
    if (selectedKit && !selectedKits.includes(selectedKit)) {
      onKitsChange([...selectedKits, selectedKit]);
      setSelectedKit('');
      setSearchValue('');
    }
  };

  // Add kit by typing and pressing Enter
  const addKitByInput = () => {
    if (searchValue.trim() && !selectedKits.includes(searchValue.trim())) {
      onKitsChange([...selectedKits, searchValue.trim()]);
      setSearchValue('');
      setSelectedKit('');
    }
  };

  // Remove kit
  const removeKit = (kit: string) => {
    onKitsChange(selectedKits.filter(k => k !== kit));
  };

  // Handle key press in search input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedKit) {
        addKit();
      } else {
        addKitByInput();
      }
    }
  };

  return (
    <div className={className}>
      <Label>Kit Numbers (Optional)</Label>
      <div className="space-y-3">
        {/* Kit selection input */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Combobox
              options={availableKits}
              value={selectedKit}
              onValueChange={setSelectedKit}
              placeholder="Search and select kit..."
              searchPlaceholder="Type to search kits..."
              emptyMessage="No kits found. Type to add a new kit."
              loading={loading}
              onSearch={handleSearch}
              className={error ? 'border-destructive' : ''}
            />
          </div>
          <Button
            type="button"
            onClick={addKit}
            variant="outline"
            disabled={!selectedKit}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Alternative: Direct input for custom kit names */}
        <div className="flex gap-2">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Or type a custom kit name..."
            onKeyPress={handleKeyPress}
            className={error ? 'border-destructive' : ''}
          />
          <Button
            type="button"
            onClick={addKitByInput}
            variant="outline"
            disabled={!searchValue.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Selected kits display */}
        {selectedKits.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedKits.map((kit) => (
              <Badge key={kit} variant="secondary" className="flex items-center gap-1">
                {kit}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => removeKit(kit)}
                />
              </Badge>
            ))}
          </div>
        )}

        {/* Error message */}
        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Help text */}
        <p className="text-xs text-muted-foreground">
          Optional: Search for existing kits or type custom kit names. Press Enter or click + to add. Leave empty to use all available questions.
        </p>
      </div>
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
